import { api } from '../../lib/api';
import type { TrackSummary } from '../tracks/TrackList';

export interface Correspondent {
  id: number;
  username: string;
  display_name: string | null;
  avatar?: string | null;
  /**
   * Whether they are here now.
   *
   * Already folded with their own visibility switch by the server, so a false
   * means "not here or not saying" and there is nothing to tell apart.
   */
  online?: boolean;
}

export interface Conversation {
  id: number;
  type: 'dm' | 'group';
  title: string | null;
  last_message_at: string | null;
  /** Exact, computed from `id > last_read_message_id` — never a stored counter. */
  unread: number;
  muted: boolean;
  preview: string;
  other: Correspondent | null;
}

export interface Message {
  id: number;
  body: string | null;
  deleted: boolean;
  created_at: string;
  /** Whether the viewer sent it, decided by the server. */
  mine: boolean;
  sender: Correspondent;
  /**
   * The attached track, resolved fresh on every read.
   *
   * Null when there is none — and also when there was one that has since been
   * made private or taken down. §6.5 calls sharing a track into a DM the single
   * highest-intent discovery path, which is exactly why it is a foreign key
   * rather than a URL in the body: a real reference knows.
   */
  track: TrackSummary | null;
}

export const messages = {
  inbox: () => api.get<{ items: Conversation[]; unread_total: number }>('/conversations'),

  start: (username: string) =>
    api.post<{ id: number; other: Correspondent }>('/conversations', { username }),

  thread: (id: number, before?: string | null) =>
    api.get<{
      conversation: { id: number; participants: Correspondent[] };
      items: Message[];
      next_cursor: string | null;
    }>(`/conversations/${id}/messages${before ? `?before=${encodeURIComponent(before)}` : ''}`),

  send: (id: number, input: { body?: string | null; track_id?: number | null }) =>
    api.post<Message>(`/conversations/${id}/messages`, input),

  markRead: (id: number, messageId?: number) =>
    api.post<{ unread_total: number }>(`/conversations/${id}/read`, {
      message_id: messageId ?? null,
    }),
};
